const en = {
  brand: "ECLENIA",
  brandSub: "Corporate Health & Workplace Wellbeing",
  nav: {
    home: "Home",
    about: "Founder",
    approach: "Approach",
    expertise: "Expertise",
    insights: "Insights",
    resources: "Resources",
    contact: "Contact",
    cta: "Book a Discovery Call",
    menu: "Menu",
    close: "Close",
  },
  home: {
    hero: {
      kicker: "Strategic Consulting · Corporate Health & Workplace Wellbeing",
      titleA: "Corporate Health",
      titleEm: "Workplace Wellbeing",
      subtitle:
        "We help companies improve employee wellbeing, to increase performance and optimize work-life balance.",
      ctaPrimary: "Book a Discovery Call",
      ctaSecondary: "Discover our method",
      badge: "Clinical Science · Strategic Thinking · Human-Centered Organizations",
      imageAlt: "Strategic dialogue between consultant and executive team",
    },
    marquee: [
      "Corporate Health",
      "Workplace Wellbeing",
      "Organizational Health",
      "Health Strategy",
      "Evidence-based prevention",
      "Employee Experience",
      "Leadership & Wellbeing",
      "Health Culture",
    ],
    stats: {
      kicker: "The evidence",
      title: "People's health is a business metric",
      items: [
        { value: "up to -51%", label: "Voluntary turnover", source: "Gallup, Q12® Meta-Analysis" },
        { value: "up to -78%", label: "Absenteeism", source: "Gallup, Q12® Meta-Analysis" },
        { value: "+14–18%", label: "Productivity", source: "Gallup, State of the Global Workplace" },
        { value: "+21–23%", label: "Profitability", source: "Gallup, Q12® Meta-Analysis" },
      ],
      footnote:
        "Figures refer to the gap between top- and bottom-quartile engagement organizations. Source: Gallup, Q12® Meta-Analysis, 11th edition (May 2024), and State of the Global Workplace.",
    },
    businessCase: {
      kicker: "The economic impact",
      title: "Why it pays off for companies",
      intro:
        "Organizational wellbeing is not an accessory benefit: it is a measurable economic lever acting on costs, operational continuity and the ability to attract talent. We see it confirmed in every organization we work with.",
      highlight: {
        value: "≈ $10 trillion",
        text: "The cost of low engagement to the global economy — 9% of global GDP (2025 data)",
        source: "Source: Gallup, State of the Global Workplace 2026",
      },
      blocks: [
        {
          title: "Lower turnover costs",
          text: "Replacing a person costs between 50% and 200% of their annual salary. Investing in health and prevention addresses one of the most underestimated cost lines at its root.",
          source: "Source: Gallup",
        },
        {
          title: "Less absenteeism and presenteeism",
          text: "Presenteeism — being at work without being fully productive — often costs more than absenteeism. People's health directly shapes operational continuity, presence and quality of work. Preventing costs less than absorbing.",
          source: null,
        },
        {
          title: "Retention and talent attraction",
          text: "A healthy workplace becomes an employer branding lever: it retains the best people and attracts those looking for credible organizations.",
          source: null,
        },
        {
          title: "Regulatory compliance",
          text: "European law requires the protection of workers' health (Directive 89/391/EEC); the obligation to specifically assess psychosocial risk was made explicit by the European Framework Agreement on Work-Related Stress (2004) and, in Italy, by Article 28 of Legislative Decree 81/2008. A structured approach reduces exposure.",
          source: "Legislative Decree 81/2008, Art. 28 · EU Framework Agreement 2004",
        },
      ],
      cta: "Book a Discovery Call",
    },
    areas: {
      kicker: "Areas of intervention",
      title: "The areas where we operate",
      link: "See all 11 areas of expertise",
      items: [
        { title: "Corporate Health", text: "Integrated corporate health strategies, from needs diagnosis to wellbeing governance." },
        { title: "Organizational Wellbeing", text: "Measuring and developing organizational wellbeing: climate, workloads, relationships." },
        { title: "Healthy Workplace Strategy", text: "Long-term strategies integrating health, prevention and business objectives." },
        { title: "Women's Health at Work", text: "Women's health across the working life cycle as a lever for equity and performance." },
        { title: "Musculoskeletal Prevention", text: "Evidence-based prevention programs for work-related musculoskeletal disorders." },
        { title: "Ergonomics", text: "Ergonomic design of workstations, environments and processes, in office and hybrid work." },
      ],
    },
    method: {
      kicker: "What we offer",
      title: "The consulting service in 4 phases",
      link: "Discover the full method",
      phases: [
        { n: "01", text: "Assessment and diagnosis of organizational health, with validated questionnaires" },
        { n: "02", text: "Company report across three levels — person, team, organization" },
        { n: "03", text: "Intervention plan shared with HR and leadership, with verifiable objectives" },
        { n: "04", text: "Continuous monitoring of results as a management indicator" },
      ],
    },
    quote: {
      text: "A healthy organization is not built on perks. It is built on strategic decisions grounded in data, science and respect for people.",
      author: "Alessia Zanella, Founder of Eclenia",
    },
    finalCta: {
      title: "We build healthy workplaces.",
      text: "We offer you a 30-minute conversation to understand where your organization stands today and where it can go.",
      cta: "Book a Discovery Call",
    },
  },
  about: {
    kicker: "Founder",
    title: "From clinical science to business strategy",
    intro:
      "My work stems from a simple conviction: people's health is the invisible infrastructure of every high-performing organization. To truly understand it, you first need to know how to read it — and that is where my journey begins.",
    chapters: [
      {
        n: "01",
        period: "Today",
        title: "Work Wellbeing / Corporate Health Consultant",
        body: "Today I work as a freelance strategic consultant alongside companies and organizations that want to build a healthy workplace. I bring into decision-making rooms the same discipline used to read a clinical case: observe, measure, diagnose, intervene, verify. Clinical practice taught me the method. Strategy gave it scale.",
        skill: "Strategic consulting · Wellbeing governance",
      },
      {
        n: "02",
        period: "2021 — 2022",
        title: "The European turning point",
        body: "European training in Work-Wellbeing Management (Erasmus+ KA202 program) gave a name and a method to what I had learned in the field: bringing health into the strategy of organizations. No longer one-off interventions, but prevention systems that are designed, measured, governed.",
        skill: "Strategic thinking · International perspective",
      },
      {
        n: "03",
        period: "In parallel",
        title: "Leadership beyond the organization",
        body: "Coordinating healthcare professionals and leading in the sports world — teams, athletes, families, teachers — confronted me with very different human ecosystems. Each context demanded a different language, a different negotiation, trust to be built. It was the training ground for my relational intelligence and my ability to communicate with very different stakeholders.",
        skill: "Communication · Relational intelligence",
      },
      {
        n: "04",
        period: "2017 — 2021",
        title: "Building and leading an organization",
        body: "Leading the opening of the Bassano del Grappa branch for a medical center with four locations between Padua and Bassano transformed my perspective. Leading a team of ten, building processes from scratch, achieving ISO 9001:2015 certification: requests grew by 200% in the first year, internal absenteeism fell from 35% to 21%, coordination efficiency improved by 21%, client satisfaction by 60%, with over 95% positive feedback. Numbers that taught me one thing: an organization's health can be measured, governed, designed.",
        skill: "Leadership · Organization · Process management",
      },
      {
        n: "05",
        period: "2014 — 2017",
        title: "The clinical foundations",
        body: "My early years in private clinical practice were my true school of observation. Thousands of cases, each with a different story: I learned to recognize the signals of stress and overload long before they become full-blown symptoms. This is where the scientific rigor of my method was born — together with the ability to read what data alone cannot yet tell.",
        skill: "Scientific rigor · Reading the signals",
      },
    ],
    network:
      "Every project is led by Eclenia, supported, when needed, by a selected network of specialists (occupational psychologists, nutritionists, ergonomists).",
    credentials: {
      kicker: "Scientific background",
      title: "The evidence-based foundations",
      items: [
        "Degree with honors in Physiotherapy",
        "Master in Musculoskeletal Rehabilitation",
        "Continuous evidence-based training",
        "European specialization course in Work-Wellbeing Management (Erasmus+ KA202)",
      ],
      note: "Clinical training is the scientific foundation of my consulting work: it is what makes every recommendation verifiable, measurable and grounded in evidence.",
    },
    eclenia: {
      title: "Why Eclenia",
      subtitle: "See beyond what is visible.",
      p1: "The name comes from the phenomenon of the eclipse — a moment when light does not disappear, but is temporarily obscured, and for this reason invites us to look more closely.",
      p2: "Many of the factors affecting health, wellbeing and performance remain invisible before they become a problem. Eclenia exists to recognize them, understand them and turn them into prevention.",
      p3: "A scientific and concrete approach that brings together people, health and organization, for healthier, more sustainable workplaces capable of generating value.",
    },
    cta: "Book a Discovery Call",
  },
  approach: {
    kicker: "The approach",
    title: "The method in four phases",
    intro:
      "Every journey follows a clear structure, adaptable to the context of each organization.",
    phases: [
      {
        n: "01",
        title: "Snapshot and diagnosis",
        body: "Every project begins with an objective snapshot of the organization's state of health. We collect data through internationally validated questionnaires and structured assessment tools, with full protection of people's privacy. Without an accurate diagnosis, every intervention is a gamble.",
        tag: "Assessment · Data · Privacy",
      },
      {
        n: "02",
        title: "Areas of intervention",
        body: "We read the data through a three-level model — person, team, organization. This makes it possible to distinguish what depends on individual behaviors from what depends on processes and culture, and to act where the leverage is greatest.",
        tag: "Person · Team · Organization",
      },
      {
        n: "03",
        title: "Shared intervention plan",
        body: "We build the plan together with leadership: concrete, verifiable objectives, clear priorities, defined resources. The wellbeing strategy enters the company's governance — it does not remain a side initiative left to good intentions.",
        tag: "Objectives · Governance · Leadership",
      },
      {
        n: "04",
        title: "Continuous reporting and monitoring",
        body: "We re-measure results over time with the same instruments used at the start. Periodic reports for leadership, clear indicators, documented course corrections. Health becomes a management indicator, like any other business metric.",
        tag: "KPIs · Reports · Re-measurement",
      },
    ],
    principles: {
      kicker: "The principles",
      title: "What never changes",
      items: [
        { title: "Evidence-based", text: "Every recommendation we make rests on scientific literature and collected data, never on trends or opinions." },
        { title: "Privacy by design", text: "People's data is handled with rigorous protocols: aggregated, anonymous, protected." },
        { title: "Measurability", text: "If it cannot be measured, it cannot be governed. Every intervention has indicators defined upfront." },
        { title: "Tailored", text: "No standard packages: every organization has its own snapshot, its own priorities, its own plan." },
      ],
    },
    cta: "Request a snapshot of your organization",
  },
  expertise: {
    kicker: "Areas of expertise",
    title: "Eleven areas, one goal: healthy organizations",
    intro:
      "Each area can be an entry point or part of an integrated strategy. We always define the exact scope in the diagnostic phase.",
    items: [
      { n: "01", title: "Corporate Health", text: "Integrated corporate health strategies: from needs diagnosis to wellbeing governance as an organizational asset." },
      { n: "02", title: "Organizational Wellbeing", text: "Measuring and developing organizational wellbeing: climate, workloads, quality of relationships, balance between demands and resources." },
      { n: "03", title: "Healthy Workplace Strategy", text: "Designing long-term strategies that integrate health, prevention and business objectives into a single plan." },
      { n: "04", title: "Women's Health at Work", text: "Programs dedicated to women's health across the stages of working life, as a lever for equity, retention and organizational performance." },
      { n: "05", title: "Musculoskeletal Prevention", text: "Evidence-based prevention programs to reduce work-related musculoskeletal disorders, among the leading causes of absence in Europe." },
      { n: "06", title: "Ergonomics", text: "Ergonomic assessment and design of workstations, environments and work processes — in the office, in production and in hybrid work." },
      { n: "07", title: "Leadership & Wellbeing", text: "Training and coaching for leaders: an organization's health starts with the behaviors of those who lead it." },
      { n: "08", title: "Employee Experience", text: "Designing work experiences where health is embedded in people's journey, from onboarding to development." },
      { n: "09", title: "Health Culture", text: "Building a shared culture of health that goes beyond individual programs and becomes a way of working." },
      { n: "10", title: "Health Promotion", text: "Health promotion initiatives consistent with data, culture and the organization's objectives." },
      { n: "11", title: "Healthy Ageing Workforce", text: "Strategies for a workforce that ages in good health: sustainable workloads, prevention and valuing experience." },
    ],
    cta: "Not sure where to start? Book a Discovery Call",
  },
  insights: {
    kicker: "Insights",
    title: "Analysis, evidence and perspectives",
    intro:
      "In this editorial section we share data-driven analysis, critical readings of the scientific literature and strategic perspectives for those who lead organizations.",
    readTime: "min read",
    readArticle: "Read the article",
    backToList: "All insights",
    sourcesLabel: "Sources",
  },
  resources: {
    kicker: "Resources",
    title: "Tools for those leading change",
    intro:
      "Briefs, self-assessments and checklists we are designing for HR leaders, executives and entrepreneurs. The library is under construction: here are the first resources on their way.",
    comingSoon: "Coming soon",
    items: [
      {
        type: "Self-Assessment",
        title: "Organizational wellbeing maturity",
        text: "A self-assessment tool to understand where your organization stands across the three levels: person, team, system.",
      },
      {
        type: "Executive Brief",
        title: "The business case for prevention",
        text: "A reasoned synthesis of the economic evidence on prevention and organizational wellbeing, written for the board.",
      },
      {
        type: "Checklist",
        title: "A healthy workplace",
        text: "The observable signals of a healthy work environment: an operational checklist for leaders and HR professionals.",
      },
    ],
    cta: "Want to be notified when they launch? Write to us",
  },
  contact: {
    kicker: "Contact",
    title: "Let's start with your organization",
    intro:
      "The first step is a conversation. Tell us about your context, goals and priorities: we will personally reply within two working days.",
    info: {
      title: "Details",
      locationLabel: "Location",
      location: "Nove (VI), Veneto — Italy",
      phoneLabel: "Phone",
      phone: "+39 340 972 6465",
      emailLabel: "Email",
      email: "info@eclenia.com",
      note: "Available for on-site advisory across Triveneto and remotely for Italian and international organizations.",
    },
    form: {
      name: "Full name",
      namePh: "Your name",
      company: "Company / Organization",
      companyPh: "Organization name",
      email: "Email",
      emailPh: "name@company.com",
      message: "Message",
      messagePh: "Tell us about your context and goals…",
      submit: "Send the request",
      sending: "Sending…",
      success: "Request sent. We will personally reply within two working days.",
      error: "Something went wrong. Please try again or write to us directly by email.",
    },
    finalCta: "Book a Discovery Call",
  },
  modal: {
    title: "Book a Discovery Call",
    subtitle: "30 minutes to understand where your organization stands and where it can go. We will get back to you to schedule date and time.",
    success: "Request sent. We will get back to you to schedule the call.",
    error: "Something went wrong. Please try again or write to us by email.",
    close: "Close",
  },
  footer: {
    claim: "Clinical Science. Strategic Thinking. Human-Centered Organizations.",
    navTitle: "Navigation",
    contactTitle: "Contact",
    ctaTitle: "We build healthy workplaces.",
    cta: "Book a Discovery Call",
    rights: "© 2026 ECLENIA — Corporate Health & Workplace Wellbeing. All rights reserved.",
    location: "Nove (VI), Veneto — Italy",
  },
};

export default en;
